// src/pages/Admin/Techniques/AddTechniqueModal.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaBookOpen,
  FaCheckCircle,
  FaEye,
  FaImage,
  FaInfoCircle,
  FaLanguage,
  FaLayerGroup,
  FaLink,
  FaPlus,
  FaSave,
  FaSpinner,
  FaTimes,
  FaVideo,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  addTechnique,
  getAllTechniques,
} from "../../../services/techniquesServices";
import { getBeltsList } from "../../../services/beltServices";

const DEFAULT_IMG = "/img/no-image.jpg";

const defaultFormData = () => ({
  nom: "",
  famille: "",
  ceinture: "",
  image1_url: "",
  video_url: "",
  traduction: "",
  information: "",
  uv2: "",
});

const inputClass =
  "min-h-[48px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-violet-400 dark:focus:ring-violet-950/60";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

const beltId = (belt) =>
  belt?.id ??
  belt?.belt_id ??
  belt?.ordre ??
  belt?.order ??
  belt?.rank ??
  Number.MAX_SAFE_INTEGER;

const beltLabel = (belt) =>
  (belt &&
    (belt.label ||
      belt.nom ||
      belt.name ||
      belt.belt_name ||
      belt.couleur ||
      belt.color ||
      belt.belt)) ||
  String(belt || "");

const normalizeFamily = (value = "") =>
  String(value || "").trim().replace(/\s+/g, " ");

const normalizeText = (value = "") => String(value || "").trim();

const toAbsolute = (url = "") => {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (!cleanUrl.startsWith("/uploads")) return cleanUrl;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${cleanUrl}`;
};

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
      <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm">
            <Icon className="text-sm" />
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">
              {title}
            </h4>

            {description && (
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">{children}</div>
    </section>
  );
}

function TechniquePreviewCard({ formData, imgSrc, imgError, onImageError }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-950 dark:bg-slate-900">
      <div className="bg-gradient-to-br from-violet-600 to-blue-600 p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
            <FaBookOpen className="text-xl" />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-white/70">
              Technique
            </p>
            <p className="text-lg font-bold">
              {formData.nom?.trim() || "Nouvelle technique"}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="overflow-hidden rounded-[1.5rem] bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
          <div className="flex h-56 items-center justify-center p-3 sm:h-64">
            <img
              src={imgSrc}
              alt="Aperçu technique"
              onError={onImageError}
              className="max-h-full max-w-full rounded-xl object-contain shadow-sm"
              draggable={false}
            />
          </div>
        </div>

        {imgError && (
          <div className="mt-3 rounded-2xl bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700 ring-1 ring-orange-100 dark:bg-orange-950/30 dark:text-orange-300 dark:ring-orange-900">
            L’image n’a pas pu être chargée. L’image par défaut sera utilisée.
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-2">
          <PreviewLine label="Famille" value={formData.famille || "—"} />
          <PreviewLine label="Ceinture" value={formData.ceinture || "—"} />
          <PreviewLine label="UV2" value={formData.uv2 ? "Renseigné" : "—"} />
        </div>
      </div>
    </section>
  );
}

function PreviewLine({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-800 dark:text-white">
        {value}
      </p>
    </div>
  );
}

export default function AddTechniqueModal({
  isOpen,
  onClose,
  refresh,
  existingFamilies,
}) {
  const dialogRef = useRef(null);

  const [formData, setFormData] = useState(defaultFormData());
  const [submitting, setSubmitting] = useState(false);
  const [imgError, setImgError] = useState(false);

  const [families, setFamilies] = useState([]);
  const [belts, setBelts] = useState([]);

  const [familyMode, setFamilyMode] = useState("existing");
  const [newFamily, setNewFamily] = useState("");

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(defaultFormData());
    setImgError(false);
    setSubmitting(false);
    setFamilyMode("existing");
    setNewFamily("");
  }, [isOpen]);

  const loadFamilies = useCallback(async () => {
    try {
      if (Array.isArray(existingFamilies) && existingFamilies.length > 0) {
        const sorted = [
          ...new Set(existingFamilies.map((item) => normalizeFamily(item))),
        ]
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "fr"));

        setFamilies(sorted);
        return;
      }

      const data = await getAllTechniques();
      const familySet = new Set();

      (Array.isArray(data) ? data : []).forEach((technique) => {
        const family = normalizeFamily(technique?.famille || "");
        if (family) familySet.add(family);
      });

      setFamilies(
        Array.from(familySet).sort((a, b) => a.localeCompare(b, "fr"))
      );
    } catch (error) {
      console.error("Erreur chargement familles:", error);
      setFamilies([]);
    }
  }, [existingFamilies]);

  const loadBelts = useCallback(async () => {
    try {
      const response = await getBeltsList();

      const items = Array.isArray(response)
        ? response
            .map((belt) => ({
              id: beltId(belt),
              label: normalizeText(beltLabel(belt)),
            }))
            .filter((belt) => belt.label)
            .sort((a, b) => Number(a.id) - Number(b.id))
        : [];

      setBelts(items);
    } catch (error) {
      console.error("Erreur chargement ceintures:", error);
      setBelts([]);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    loadFamilies();
    loadBelts();
  }, [isOpen, loadFamilies, loadBelts]);

  useEffect(() => {
    if (!isOpen) return;

    const root = window.document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    const timer = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
      root.style.overflow = previousOverflow;
    };
  }, [isOpen, handleClose]);

  const imgSrc = useMemo(() => {
    if (imgError || !formData.image1_url?.trim()) return DEFAULT_IMG;
    return toAbsolute(formData.image1_url);
  }, [formData.image1_url, imgError]);

  const chosenFamily = useMemo(
    () => normalizeFamily(formData.famille),
    [formData.famille]
  );

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!formData.nom.trim()) return false;
    if (familyMode === "new" && !chosenFamily) return false;
    return true;
  }, [chosenFamily, familyMode, formData.nom, submitting]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (name === "image1_url") {
      setImgError(false);
    }
  };

  const handleExistingFamilyChange = (event) => {
    const value = normalizeFamily(event.target.value);

    setFamilyMode("existing");
    setNewFamily("");

    setFormData((previous) => ({
      ...previous,
      famille: value,
    }));
  };

  const handleNewFamilyChange = (event) => {
    const value = normalizeFamily(event.target.value);

    setFamilyMode("new");
    setNewFamily(value);

    setFormData((previous) => ({
      ...previous,
      famille: value,
    }));
  };

  const setExistingMode = () => {
    setFamilyMode("existing");
    setNewFamily("");

    setFormData((previous) => ({
      ...previous,
      famille: "",
    }));
  };

  const setNewMode = () => {
    setFamilyMode("new");
    setNewFamily("");

    setFormData((previous) => ({
      ...previous,
      famille: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.nom.trim()) {
      toast.error("Le nom est requis.");
      return;
    }

    if (familyMode === "new" && !chosenFamily) {
      toast.error("Veuillez saisir le nom de la nouvelle famille.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        nom: formData.nom.trim(),
        famille: chosenFamily || null,
        ceinture: formData.ceinture?.trim() || null,
        image1_url:
          formData.image1_url?.trim() && !imgError
            ? formData.image1_url.trim()
            : DEFAULT_IMG,
        video_url: formData.video_url?.trim() || null,
        traduction: formData.traduction?.trim() || null,
        information: formData.information?.trim() || null,
        uv2: formData.uv2?.trim() || null,
      };

      await addTechnique(payload);

      if (payload.famille) {
        setFamilies((previous) =>
          previous.includes(payload.famille)
            ? previous
            : [...previous, payload.famille].sort((a, b) =>
                a.localeCompare(b, "fr")
              )
        );
      }

      toast.success("Technique créée avec succès.");

      refresh?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur création technique:", error);
      toast.error("Erreur lors de la création de la technique.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Ajouter une technique"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="relative flex max-h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-t-[2rem] bg-slate-50 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 sm:max-h-[90dvh] sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-violet-600 to-pink-600 px-4 py-5 text-white sm:px-6">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/75">
                Bibliothèque technique
              </p>

              <h3 className="mt-1 text-xl font-bold text-white sm:text-2xl">
                Ajouter une technique
              </h3>

              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/85">
                Ajoutez une technique avec sa famille, sa ceinture, son image,
                sa vidéo et ses informations pédagogiques.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 disabled:opacity-60"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="technique-modal-scroll min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              <aside className="space-y-5">
                <TechniquePreviewCard
                  formData={formData}
                  imgSrc={imgSrc}
                  imgError={imgError}
                  onImageError={() => setImgError(true)}
                />

                <SectionCard
                  icon={FaInfoCircle}
                  title="Résumé"
                  description="Contrôle rapide avant création."
                >
                  <div className="space-y-3">
                    <PreviewLine
                      label="Nom"
                      value={formData.nom.trim() || "—"}
                    />
                    <PreviewLine
                      label="Traduction"
                      value={formData.traduction.trim() || "—"}
                    />
                    <PreviewLine
                      label="Vidéo"
                      value={formData.video_url.trim() ? "Lien renseigné" : "—"}
                    />
                  </div>

                  <div className="mt-4 rounded-2xl bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:ring-blue-900">
                    Après création, tu pourras téléverser une image depuis la
                    fenêtre d’édition si besoin.
                  </div>
                </SectionCard>
              </aside>

              <main className="space-y-5">
                <SectionCard
                  icon={FaBookOpen}
                  title="Informations principales"
                  description="Le nom est obligatoire. La famille peut être existante ou nouvelle."
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className={labelClass}>
                        Nom de la technique <span className="text-pink-600">*</span>
                      </label>

                      <input
                        type="text"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        disabled={submitting}
                        placeholder="Ex : O-soto-gari"
                        className={inputClass}
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className={labelClass}>Mode famille</label>

                      <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
                        <button
                          type="button"
                          onClick={setExistingMode}
                          disabled={submitting}
                          className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition ${
                            familyMode === "existing"
                              ? "bg-violet-600 text-white shadow-sm"
                              : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                          }`}
                        >
                          <FaLayerGroup />
                          Existante
                        </button>

                        <button
                          type="button"
                          onClick={setNewMode}
                          disabled={submitting}
                          className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition ${
                            familyMode === "new"
                              ? "bg-violet-600 text-white shadow-sm"
                              : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                          }`}
                        >
                          <FaPlus />
                          Nouvelle
                        </button>
                      </div>
                    </div>

                    {familyMode === "existing" ? (
                      <div className="sm:col-span-2">
                        <label className={labelClass}>Famille existante</label>

                        <select
                          name="famille"
                          value={formData.famille}
                          onChange={handleExistingFamilyChange}
                          disabled={submitting}
                          className={inputClass}
                        >
                          <option value="">— Aucune famille —</option>

                          {families.map((family) => (
                            <option key={family} value={family}>
                              {family}
                            </option>
                          ))}
                        </select>

                        <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                          Les familles sont récupérées depuis les techniques
                          existantes.
                        </p>
                      </div>
                    ) : (
                      <div className="sm:col-span-2">
                        <label className={labelClass}>
                          Nouvelle famille <span className="text-pink-600">*</span>
                        </label>

                        <input
                          type="text"
                          value={newFamily}
                          onChange={handleNewFamilyChange}
                          disabled={submitting}
                          placeholder="Ex : Nage-waza, Katame-waza, Shime-waza..."
                          className={inputClass}
                        />
                      </div>
                    )}

                    <div>
                      <label className={labelClass}>Ceinture</label>

                      <select
                        name="ceinture"
                        value={formData.ceinture}
                        onChange={handleChange}
                        disabled={submitting}
                        className={inputClass}
                      >
                        <option value="">— Aucune —</option>

                        {belts.map((belt) => (
                          <option key={`${belt.id}-${belt.label}`} value={belt.label}>
                            {belt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Traduction</label>

                      <div className="relative">
                        <FaLanguage className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="traduction"
                          value={formData.traduction}
                          onChange={handleChange}
                          disabled={submitting}
                          placeholder="Ex : Grande projection extérieure"
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaImage}
                  title="Illustration et vidéo"
                  description="Ajoutez une URL d’image et, si besoin, un lien vidéo."
                >
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className={labelClass}>URL de l’image</label>

                      <div className="relative">
                        <FaImage className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="image1_url"
                          placeholder="https://…/image.jpg ou /uploads/..."
                          value={formData.image1_url}
                          onChange={handleChange}
                          disabled={submitting}
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>URL de la vidéo</label>

                      <div className="relative">
                        <FaVideo className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="url"
                          name="video_url"
                          placeholder="https://youtube.com/..."
                          value={formData.video_url}
                          onChange={handleChange}
                          disabled={submitting}
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaInfoCircle}
                  title="Informations pédagogiques"
                  description="Ajoutez des repères utiles pour les enseignants ou les élèves."
                >
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>Informations</label>

                      <textarea
                        name="information"
                        rows={5}
                        placeholder="Détails, repères pédagogiques, erreurs fréquentes, variantes…"
                        value={formData.information}
                        onChange={handleChange}
                        disabled={submitting}
                        className={`${inputClass} resize-none leading-relaxed`}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>UV2</label>

                      <textarea
                        name="uv2"
                        rows={5}
                        placeholder="Fiche UV2, points clés, enchaînements, situations..."
                        value={formData.uv2}
                        onChange={handleChange}
                        disabled={submitting}
                        className={`${inputClass} resize-none leading-relaxed`}
                      />
                    </div>
                  </div>
                </SectionCard>
              </main>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Fermer
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Créer la technique
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .technique-modal-scroll::-webkit-scrollbar {
          width: 10px;
        }

        .technique-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.45);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }

        .technique-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .dark .technique-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.35);
          border: 3px solid transparent;
          background-clip: content-box;
        }
      `}</style>
    </div>,
    window.document.body
  );
}